import { Controller } from "@hotwired/stimulus"
import { positionFixedDropdown } from "../utils/dropdown_utils"

export default class extends Controller {
  static targets = ["value", "trigger", "label", "dropdown", "search", "optionsContainer", "option", "clearBtn", "loading", "loadingMore"]

  static values = {
    searchUrl: String,
    placeholder: { type: String, default: "Select..." },
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this.debounceTimer = null
    this.currentQuery = ""
    this.offset = 0
    this.hasMore = true
    this.loading = false
    this.loadedIds = new Set()
    this.abortController = null
    this.repositionHandler = this.repositionIfOpen.bind(this)
  }

  disconnect() {
    if (this.abortController) this.abortController.abort()
    clearTimeout(this.debounceTimer)
    this.teardownFixedListeners()
  }

  toggle(event) {
    event.preventDefault()
    if (this.dropdownTarget.classList.contains("hidden")) {
      this.showDropdown()
    } else {
      this.hideDropdown()
    }
  }

  showDropdown() {
    if (!this.dropdownTarget.classList.contains("hidden")) return

    this.dropdownTarget.classList.remove("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    positionFixedDropdown(this.dropdownTarget, this.triggerTarget)
    this.setupFixedListeners()
    this.searchTarget.value = ""
    this.currentQuery = ""
    this.offset = 0
    this.hasMore = true
    this.loadedIds.clear()
    this.optionsContainerTarget.replaceChildren()
    this.showLoading()
    this.fetchOptions()
    requestAnimationFrame(() => this.searchTarget.focus())
  }

  hideDropdown() {
    this.clearOptionFocus()
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.triggerTarget.removeAttribute("aria-activedescendant")
    this.dropdownTarget.classList.add("hidden")
    this.teardownFixedListeners()
  }

  setupFixedListeners() {
    window.addEventListener("resize", this.repositionHandler)
    window.addEventListener("scroll", this.repositionHandler, true)
  }

  teardownFixedListeners() {
    if (!this.repositionHandler) return
    window.removeEventListener("resize", this.repositionHandler)
    window.removeEventListener("scroll", this.repositionHandler, true)
  }

  repositionIfOpen() {
    if (this.dropdownTarget.classList.contains("hidden")) return
    positionFixedDropdown(this.dropdownTarget, this.triggerTarget)
  }

  handleKeydown(event) {
    const { key } = event

    if (key === "Escape") {
      event.preventDefault()
      this.hideDropdown()
      this.triggerTarget.focus()
      return
    }

    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault()
      this.moveOptionFocus(key === "ArrowDown" ? 1 : -1)
      return
    }

    if (key === "Enter") {
      event.preventDefault()
      const focused = this.optionTargets.find((el) => el.classList.contains("ts-option-focused"))
      if (focused) focused.click()
    }
  }

  moveOptionFocus(direction) {
    const options = this.optionTargets
    if (options.length === 0) return

    const currentIndex = options.findIndex((el) => el.classList.contains("ts-option-focused"))

    this.clearOptionFocus()

    const nextIndex = direction > 0
      ? (currentIndex < options.length - 1 ? currentIndex + 1 : 0)
      : (currentIndex > 0 ? currentIndex - 1 : options.length - 1)

    options[nextIndex].classList.add("ts-option-focused")
    options[nextIndex].scrollIntoView({ block: "nearest" })
    if (options[nextIndex].id) {
      this.triggerTarget.setAttribute("aria-activedescendant", options[nextIndex].id)
    }
  }

  clearOptionFocus() {
    this.optionTargets.forEach((el) => el.classList.remove("ts-option-focused"))
  }

  onSearchInput() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      const query = this.searchTarget.value.trim()
      if (query !== this.currentQuery) {
        if (this.abortController) {
          this.abortController.abort()
          this.loading = false
        }
        this.currentQuery = query
        this.offset = 0
        this.hasMore = true
        this.loadedIds.clear()
        this.optionsContainerTarget.replaceChildren()
        this.showLoading()
        this.fetchOptions()
      }
    }, this.debounceValue)
  }

  onScroll() {
    if (this.loading || !this.hasMore) return
    const container = this.optionsContainerTarget
    if (container.scrollHeight - container.scrollTop - container.clientHeight < 50) {
      this.fetchOptions()
    }
  }

  async fetchOptions() {
    if (this.loading || !this.hasMore) return

    this.loading = true
    const request = new AbortController()
    this.abortController = request
    if (this.offset > 0) this.showLoadingMore()

    const url = new URL(this.searchUrlValue, window.location.origin)
    if (this.currentQuery) url.searchParams.set("q", this.currentQuery)
    url.searchParams.set("offset", this.offset)

    try {
      const response = await fetch(url, { signal: request.signal })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      const wasFirstPage = this.offset === 0
      this.hasMore = data.has_more
      this.offset += data.owners.length

      this.hideLoading()
      this.appendOptions(data.owners)

      if (!this.dropdownTarget.classList.contains("hidden") && wasFirstPage) {
        positionFixedDropdown(this.dropdownTarget, this.triggerTarget)
      }

      if (data.owners.length === 0 && this.offset === 0) this.showNoResults()
    } catch (error) {
      if (error.name === "AbortError") return
      this.hideLoading()
      this.showError()
    } finally {
      if (this.abortController === request) {
        this.loading = false
        this.hideLoadingMore()
      }
    }
  }

  appendOptions(owners) {
    const selectedId = String(this.valueTarget.value || "")

    owners.forEach((owner) => {
      const id = String(owner.id)
      if (this.loadedIds.has(id)) return
      this.loadedIds.add(id)

      const option = document.createElement("div")
      option.id = `${this.element.id}-option-${id}`
      option.className = "px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
      if (id === selectedId) option.classList.add("bg-blue-50", "font-medium")
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", id === selectedId ? "true" : "false")
      option.setAttribute("data-async-single-select-target", "option")
      option.dataset.id = id
      option.dataset.name = owner.name
      option.dataset.role = owner.role || ""
      option.dataset.action = "click->async-single-select#selectOption"
      this.fillNameWithRole(option, owner.name, owner.role)

      this.optionsContainerTarget.appendChild(option)
    })
  }

  fillNameWithRole(element, name, role) {
    element.replaceChildren()
    element.appendChild(document.createTextNode(name || ""))
    if (!role) return

    element.appendChild(document.createTextNode(" · "))
    const roleEl = document.createElement("span")
    roleEl.className = "text-xs"
    roleEl.textContent = role
    element.appendChild(roleEl)
  }

  selectOption(event) {
    event.stopPropagation()
    this.clearOptionFocus()
    const option = event.currentTarget
    const id = option.dataset.id
    const name = option.dataset.name
    const role = option.dataset.role

    this.valueTarget.value = id
    this.fillNameWithRole(this.labelTarget, name, role)
    this.labelTarget.classList.remove("text-gray-400")
    this.labelTarget.classList.add("text-gray-900")
    this.toggleClearButton(true)
    this.hideDropdown()
  }

  clearValue(event) {
    event.preventDefault()
    event.stopPropagation()
    this.valueTarget.value = ""
    this.labelTarget.textContent = this.placeholderValue
    this.labelTarget.classList.remove("text-gray-900")
    this.labelTarget.classList.add("text-gray-400")
    this.toggleClearButton(false)
    this.hideDropdown()
  }

  toggleClearButton(show) {
    if (!this.hasClearBtnTarget) return
    this.clearBtnTarget.classList.toggle("hidden", !show)
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
      this.loadingTarget.classList.add("block")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
      this.loadingTarget.classList.remove("block")
    }
  }

  showNoResults() {
    const msg = document.createElement("div")
    msg.className = "px-3 py-2 text-sm text-gray-500 text-center"
    msg.textContent = "No results found"
    this.optionsContainerTarget.replaceChildren(msg)
  }

  showLoadingMore() {
    if (this.hasLoadingMoreTarget) this.loadingMoreTarget.classList.remove("hidden")
  }

  hideLoadingMore() {
    if (this.hasLoadingMoreTarget) this.loadingMoreTarget.classList.add("hidden")
  }

  showError() {
    const msg = document.createElement("div")
    msg.className = "px-3 py-2 text-sm text-red-500 text-center"
    msg.textContent = "Failed to load. Please try again."
    this.optionsContainerTarget.replaceChildren(msg)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) this.hideDropdown()
  }
}
